import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, ArrowLeft, Copy, Check, Send, FileDown, GitCompare, RefreshCw, Plus, Flower2, Gavel, X } from 'lucide-react';
import {
  getMatter, patchClause, patchMatter, protocolUrl, getProtocolBlob, getProtocolPreview,
  addVersion, saveProtocolVersion, compareVersions, analyzeVersion,
  generateManagerLetter, downloadManagerLetterDocx,
} from '../api/matters';
import FileDrop from '../components/FileDrop';
import CompareResults from '../components/CompareResults';
import MatterChat from './MatterChat';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import type { MatterDetail, Clause, MatterVersion, CompareResponse, ProtocolPreview } from '../types';

const MATTER_STATUSES = ['Анализ', 'На согласовании', 'Протокол', 'Отправлено', 'Получен ответ', 'Подписан'];

const SEVERITY_BADGE: Record<string, string> = {
  'высокий': 'bg-red-100 text-red-800',
  'средний': 'bg-amber-100 text-amber-800',
  'низкий': 'bg-green-100 text-green-800',
};
const ACTION_BADGE: Record<string, string> = {
  'согласовать': 'bg-blue-100 text-blue-800',
  'изменить': 'bg-purple-100 text-purple-800',
  'исключить': 'bg-red-100 text-red-800',
  'добавить': 'bg-emerald-100 text-emerald-800',
};
const FILTERS = [
  { id: 'all', label: 'Все пункты' },
  { id: 'risks', label: 'Риски' },
  { id: 'protocol', label: 'В протоколе' },
];

// Split a long text into readable paragraphs: keep explicit line breaks; if a
// single long block has none, split by sentences (~3 per paragraph). Avoids
// breaking «0,1 %», «10 000», «ст. 333» (only splits at .!? + space + capital).
function toParagraphs(text: string): string[] {
  const t = (text || '').trim();
  if (!t) return [];
  const byNl = t.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (byNl.length > 1) return byNl;
  if (t.length <= 280) return [t];
  const sentences = t.split(/(?<=[.!?])\s+(?=[А-ЯЁA-Z])/);
  const out: string[] = [];
  let buf: string[] = [];
  let len = 0;
  for (const s of sentences) {
    buf.push(s); len += s.length;
    if (buf.length >= 3 || len >= 260) { out.push(buf.join(' ')); buf = []; len = 0; }
  }
  if (buf.length) out.push(buf.join(' '));
  return out;
}

function Prose({ text, className = '' }: { text: string; className?: string }) {
  const paras = toParagraphs(text);
  if (paras.length === 0) return null;
  return (
    <div className={`font-serif text-[15px] leading-7 text-gray-700 max-w-prose space-y-2 ${className}`}>
      {paras.map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>)}
    </div>
  );
}

function ClauseItem({ matterId, clause, onPatch }:
  { matterId: number; clause: Clause; onPatch: (id: number, partial: Partial<Clause>) => void }) {
  const [toolsOpen, setToolsOpen] = useState(false);
  const [textExpanded, setTextExpanded] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [conflict, setConflict] = useState<string | null>(null); // external text that arrived while editing
  const taRef = useRef<HTMLTextAreaElement>(null);
  const [editText, setEditText] = useState(clause.current_text);
  const baseRef = useRef(clause.current_text);   // last known server text
  const editTextRef = useRef(clause.current_text); // latest editor value (for save loop)
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [gavelHit, setGavelHit] = useState(false);
  const [flowerHit, setFlowerHit] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Baseline "AI redaction" the editor is compared against (analysis suggestion).
  const [aiText, setAiText] = useState(clause.suggested_text);

  const toggleProtocol = async () => {
    setGavelHit(true);
    setTimeout(() => setGavelHit(false), 400);
    try {
      const c = await patchClause(matterId, clause.id, { include_in_protocol: !clause.include_in_protocol });
      onPatch(c.id, c);
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка'); }
  };

  useEffect(() => { editTextRef.current = editText; }, [editText]);
  // Adopt EXTERNAL changes to the clause text (e.g., managers reply), but never
  // clobber the user's unsaved edits or echo our own save.
  useEffect(() => {
    if (clause.current_text === baseRef.current) return; // our own echo / no change
    if (editText === baseRef.current) {                  // editor clean -> adopt new text
      setEditText(clause.current_text);
      baseRef.current = clause.current_text;
      setConflict(null);
    } else {                                             // editor dirty -> don't clobber edits
      baseRef.current = clause.current_text;
      setConflict(clause.current_text);
      setToolsOpen(true);                                // reveal the conflict banner
    }
  }, [clause.current_text]); // eslint react-hooks/exhaustive-deps intentionally narrow
  useEffect(() => { setAiText(clause.suggested_text); }, [clause.suggested_text]);

  const sev = SEVERITY_BADGE[clause.severity] || '';
  const dirty = editText !== clause.current_text;

  const toggleRisk = async (val: boolean) => {
    setFlowerHit(true);
    setTimeout(() => setFlowerHit(false), 450);
    try {
      const c = await patchClause(matterId, clause.id, { is_risk: val });
      onPatch(c.id, c);
      if (val) setToolsOpen(true);
    } catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка'); }
  };
  const save = async () => {
    if (savingRef.current) return;  // one save in flight; trailing edits flushed below
    savingRef.current = true; setSaving(true); setErr(null);
    try {
      let sent = editTextRef.current;
      let expected = baseRef.current;             // server's last-known value (optimistic lock)
      for (let i = 0; i < 20; i++) {
        const c = await patchClause(matterId, clause.id, { current_text: sent, base_text: expected });
        baseRef.current = sent; expected = sent;  // server now holds `sent`
        // Merge ONLY the field we changed; the full response may be stale w.r.t.
        // messages/chat_suggestion/toggles changed during this save.
        onPatch(c.id, { current_text: c.current_text });
        if (editTextRef.current === sent) break;  // no newer edits -> done
        sent = editTextRef.current;               // saved while typing -> save the tail
      }
      setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1500);
    }
    catch (e: any) {
      if (e?.response?.status === 409) {
        // Clause changed on the server mid-save -> surface the conflict, don't overwrite.
        const serverText = e.response.data?.detail?.current_text ?? '';
        baseRef.current = serverText;
        onPatch(clause.id, { current_text: serverText });
        if (serverText !== editTextRef.current) { setConflict(serverText); setToolsOpen(true); }
      } else {
        const d = e?.response?.data?.detail;
        setErr(typeof d === 'string' ? d : 'Ошибка сохранения');
      }
    }
    finally { savingRef.current = false; setSaving(false); }
  };

  // Auto-save the редакция on any change (debounced); no Save button.
  useEffect(() => {
    if (conflict) return;                       // unresolved external change -> wait for user
    if (editText === clause.current_text) return;
    const t = window.setTimeout(() => { void save(); }, 700);
    return () => window.clearTimeout(t);
  }, [editText, clause.current_text, conflict]);

  // Grow the editor to fit its content (compact for short clauses).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 480) + 'px';
  }, [editText, toolsOpen]);

  const applySuggested = (text: string) => setEditText(text);
  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {}
  };

  const clauseText = clause.original_text || clause.current_text || '';
  const longText = clauseText.length > 360;

  return (
    <div className={`bg-white rounded-xl border ${clause.is_risk ? 'border-l-4 ' + (clause.severity === 'высокий' ? 'border-l-red-500' : clause.severity === 'средний' ? 'border-l-amber-500' : 'border-l-green-400') : 'border-gray-200'} shadow-sm p-3 space-y-2`}>
      {/* Header: number + chips on the left, toggles together on the right */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-sm font-semibold text-gray-800">{clause.number ? `п. ${clause.number}` : (clause.title || 'Без номера')}</span>
          {clause.is_risk && clause.severity && <span className={`text-[10px] px-2 py-0.5 rounded-full ${sev}`}>{clause.severity}</span>}
          {clause.is_risk && clause.action && <span className={`text-[10px] px-2 py-0.5 rounded-full ${ACTION_BADGE[clause.action] || 'bg-gray-100 text-gray-700'}`}>{clause.action}</span>}
          {clause.carried_over && <span className="text-[10px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-800" title="Перенесено с прошлого витка">перенесено</span>}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={() => toggleRisk(!clause.is_risk)}
            title={clause.is_risk ? 'Снять риск' : 'Пометить как риск'}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition ${
              clause.is_risk ? 'bg-amber-50 text-amber-600 border-amber-200' : 'bg-white text-gray-500 border-gray-200 hover:border-amber-300'
            }`}
          >
            <Flower2 className={`w-4 h-4 ${flowerHit ? 'animate-flower' : ''}`} fill={clause.is_risk ? 'currentColor' : 'none'} />
            Риск
          </button>
          {clause.is_risk && (
            <button
              type="button"
              onClick={toggleProtocol}
              title={clause.include_in_protocol ? 'Убрать из протокола' : 'В протокол разногласий'}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium transition ${
                clause.include_in_protocol
                  ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white border-transparent shadow-sm'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-pink-300'
              }`}
            >
              <Gavel className={`w-4 h-4 ${gavelHit ? 'animate-gavel' : ''}`} />
              {clause.include_in_protocol ? 'В протоколе' : 'В протокол'}
            </button>
          )}
        </div>
      </div>

      {/* Clause text — visible immediately, clamped if long */}
      {clauseText && (
        <div>
          <div className={`bg-gray-50 rounded-lg p-3 overflow-auto ${longText && !textExpanded ? 'max-h-40' : ''}`}>
            <Prose text={clauseText} />
          </div>
          {longText && (
            <button onClick={() => setTextExpanded((e) => !e)} className="mt-1 text-xs text-gray-500 hover:text-pink-600">
              {textExpanded ? 'Свернуть' : 'Развернуть'}
            </button>
          )}
        </div>
      )}

      {/* Risk summary — labelled, split into «Риск» and «Рекомендация» */}
      {clause.is_risk && (clause.title || clause.description || clause.recommendation) && (
        <div className="space-y-2">
          {(clause.title || clause.description) && (
            <div className="bg-rose-50 border border-rose-100 rounded-lg p-3 space-y-2">
              {clause.title && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-500 mb-0.5">Риск</p>
                  <p className="text-[15px] font-semibold text-gray-800">{clause.title}</p>
                </div>
              )}
              {clause.description && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-400 mb-0.5">В чем риск?</p>
                  <Prose text={clause.description} />
                </div>
              )}
            </div>
          )}
          {clause.recommendation && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600 mb-0.5">Рекомендация</p>
              <Prose text={clause.recommendation} />
            </div>
          )}
        </div>
      )}

      {/* Risk tools: inline editor (under toggle) + chat in a modal */}
      {clause.is_risk && (
        <>
          <div className="flex items-center gap-3">
            <button onClick={() => setToolsOpen((o) => !o)}
              className="text-xs font-medium text-pink-600 hover:text-pink-700">
              {toolsOpen ? 'Скрыть редактор ↑' : 'Редактировать ↓'}
            </button>
          </div>

          {toolsOpen && (
            <div className="pt-1">
              {conflict !== null && (
                <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  <p className="mb-1.5">Пункт изменился извне (ответ менеджера или повторный анализ), пока вы редактировали. Что оставить?</p>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setConflict(null)}
                      className="px-2 py-1 rounded-md bg-amber-600 text-white hover:bg-amber-700">Оставить мою редакцию</button>
                    <button onClick={() => { setEditText(clause.current_text); baseRef.current = clause.current_text; setConflict(null); }}
                      className="px-2 py-1 rounded-md border border-amber-300 bg-white text-amber-700 hover:bg-amber-100">Взять новую версию</button>
                  </div>
                </div>
              )}
              {/* Editor — autosizing, autosaves on change */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-gray-600">Редакция пункта</label>
                  <span className="text-[11px] text-gray-400">
                    {saving ? 'Сохранение…' : savedFlash ? 'Сохранено' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {aiText && editText !== aiText && (
                    <button onClick={() => applySuggested(aiText)} className="text-xs text-emerald-700 hover:underline">Вернуть редакцию от ИИ</button>
                  )}
                  <button onClick={() => copy(editText)} className="text-xs text-gray-500 hover:text-pink-600 flex items-center gap-1">
                    {copied ? <><Check className="w-3 h-3 text-green-500" />Скопировано</> : <><Copy className="w-3 h-3" />Копировать</>}
                  </button>
                </div>
              </div>
              <textarea ref={taRef} value={editText} rows={2}
                onChange={(e) => setEditText(e.target.value)}
                onBlur={() => { if (dirty && !conflict) void save(); }}
                className="w-full min-h-[4.5rem] max-h-[30rem] overflow-auto px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] leading-relaxed resize-none outline-none focus:ring-2 focus:ring-pink-400/50" />
            </div>
          )}
        </>
      )}

      {err && <p className="text-xs text-red-600">{err}</p>}
    </div>
  );
}

function VersionsPanel({ matter, onReload, onMatterChanged }:
  { matter: MatterDetail; onReload: () => Promise<void>; onMatterChanged: (m: MatterDetail) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('contract');
  const [source, setSource] = useState('counterparty');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [cmpA, setCmpA] = useState<string>('');
  const [cmpB, setCmpB] = useState<string>('');
  const [cmp, setCmp] = useState<CompareResponse | null>(null);
  const [cmpLabels, setCmpLabels] = useState<{ a: string; b: string } | null>(null);
  const versions: MatterVersion[] = matter.versions || [];

  const upload = async () => {
    if (!file && !text.trim()) { setErr('Загрузите файл или вставьте текст'); return; }
    setBusy('upload'); setErr(null);
    try { await addVersion(matter.id, { file, text, label, kind, source }); setFile(null); setText(''); setLabel(''); await onReload(); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка'); } finally { setBusy(''); }
  };
  const saveOurPR = async () => {
    setBusy('pr'); setErr(null);
    try { await saveProtocolVersion(matter.id); await onReload(); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка'); } finally { setBusy(''); }
  };
  const doCompare = async () => {
    if (!cmpA || !cmpB) { setErr('Выберите две версии'); return; }
    if (cmpA === cmpB) { setErr('Выберите две разные версии'); return; }
    setBusy('cmp'); setErr(null); setCmp(null);
    const la = versions.find((v) => String(v.id) === cmpA)?.label || 'A';
    const lb = versions.find((v) => String(v.id) === cmpB)?.label || 'B';
    try { setCmp(await compareVersions(matter.id, Number(cmpA), Number(cmpB))); setCmpLabels({ a: la, b: lb }); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка сверки'); } finally { setBusy(''); }
  };
  const reanalyze = async (vid: number) => {
    if (!window.confirm('Перезапустить анализ этой версии? Тексты рисков обновятся, ваши статусы/решения/правки/чаты сохранятся по номерам пунктов.')) return;
    setBusy('an' + vid); setErr(null);
    try { onMatterChanged(await analyzeVersion(matter.id, vid)); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Ошибка анализа'); } finally { setBusy(''); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Версии и витки</h3>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      <div className="space-y-1.5 mb-3">
        {versions.map((v) => (
          <div key={v.id} className="flex items-center gap-2 text-sm flex-wrap">
            <span className={`px-2 py-0.5 rounded-full text-xs ${v.id === matter.active_version_id ? 'bg-pink-500 text-white' : 'bg-gray-100 text-gray-600'}`}>{v.label}</span>
            <span className="text-xs text-gray-500">{v.kind === 'protocol' ? 'ПР' : 'договор'} · {v.source === 'ours' ? 'наш' : 'контрагент'} · пунктов {v.clauses_count}</span>
            {v.kind === 'contract' && v.source === 'counterparty' && (
              <Button size="sm" variant="secondary" className="ml-auto" onClick={() => reanalyze(v.id)}
                disabled={!!busy} loading={busy === 'an' + v.id}>
                {busy !== 'an' + v.id && <RefreshCw className="w-3 h-3" />} Новый виток
              </Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <GitCompare className="w-4 h-4 text-gray-400" />
        <Select value={cmpA} onChange={setCmpA} className="w-36"
          placeholder="версия A"
          options={versions.map((v) => ({ value: String(v.id), label: v.label }))} />
        <Select value={cmpB} onChange={setCmpB} className="w-36"
          placeholder="версия B"
          options={versions.map((v) => ({ value: String(v.id), label: v.label }))} />
        <Button size="sm" onClick={doCompare} disabled={!!busy} loading={busy === 'cmp'}>Сверить</Button>
        <Button size="sm" variant="secondary" onClick={saveOurPR} disabled={!!busy} loading={busy === 'pr'}>
          Сохранить наш ПР как версию
        </Button>
      </div>

      <details>
        <summary className="text-sm text-gray-600 cursor-pointer flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Загрузить новую версию</summary>
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap gap-2">
            <Select value={kind} onChange={setKind} className="w-36"
              options={[{ value: 'contract', label: 'договор' }, { value: 'protocol', label: 'протокол' }]} />
            <Select value={source} onChange={setSource} className="w-40"
              options={[{ value: 'counterparty', label: 'контрагента' }, { value: 'ours', label: 'наш' }]} />
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="метка (необяз.)"
              className="text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-pink-400/50" />
          </div>
          <FileDrop file={file} onFileChange={setFile} onError={setErr} disabled={!!busy || text.trim().length > 0} />
          <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!!busy || !!file}
            placeholder="или вставьте текст версии..." className="w-full h-20 text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none outline-none focus:ring-2 focus:ring-pink-400/50" />
          <Button size="sm" onClick={upload} disabled={!!busy} loading={busy === 'upload'}>Добавить версию</Button>
        </div>
      </details>

      {cmp && (
        <div className="mt-4">
          {cmpLabels && (
            <p className="text-xs text-gray-500 mb-1">
              Сверка: <b>Документ 1</b> = {cmpLabels.a} · <b>Документ 2</b> = {cmpLabels.b}
            </p>
          )}
          <CompareResults results={cmp} onSwapAndRetry={() => setCmp(null)} />
        </div>
      )}
    </div>
  );
}

function ManagerPanel({ matter }: { matter: MatterDetail }) {
  const [letter, setLetter] = useState(matter.manager_letter || '');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const gen = async () => {
    setBusy('gen'); setErr(null);
    try { const r = await generateManagerLetter(matter.id); setLetter(r.text); }
    catch (e: any) { setErr(e?.response?.data?.detail || 'Не удалось сформировать письмо'); } finally { setBusy(''); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(letter); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const dl = async () => {
    setBusy('dl'); setErr(null);
    try { await downloadManagerLetterDocx(matter.id, letter); }
    catch { setErr('Не удалось скачать .docx'); } finally { setBusy(''); }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Письмо менеджерам</h3>
      {err && <p className="text-xs text-red-600 mb-2">{err}</p>}

      <div className="flex flex-wrap gap-2 mb-2">
        <Button size="sm" onClick={gen} loading={busy === 'gen'}>
          {letter ? 'Пересоздать письмо' : 'Сформировать письмо'}
        </Button>
        {letter && (
          <Button size="sm" variant="secondary" onClick={copy}>
            {copied ? <><Check className="w-4 h-4" /> Скопировано</> : <><Copy className="w-4 h-4" /> Копировать</>}
          </Button>
        )}
        {letter && (
          <Button size="sm" variant="secondary" onClick={dl} loading={busy === 'dl'}>
            <FileDown className="w-4 h-4" /> .docx
          </Button>
        )}
      </div>
      {letter && (
        <textarea value={letter} onChange={(e) => setLetter(e.target.value)}
          onBlur={() => patchMatter(matter.id, { manager_letter: letter }).catch(() => { /* ignore */ })}
          className="w-full h-72 text-[15px] leading-relaxed border border-gray-200 rounded-xl px-3 py-2.5 resize-y outline-none focus:ring-2 focus:ring-pink-400/50" />
      )}
    </div>
  );
}

function ProtocolPreviewModal({ matter, onClose, onSaved }:
  { matter: MatterDetail; onClose: () => void; onSaved: (m: MatterDetail) => void }) {
  const [intro, setIntro] = useState(matter.protocol_intro || '');
  const [num, setNum] = useState(matter.contract_number || '');
  const [date, setDate] = useState(matter.contract_date || '');
  const [preview, setPreview] = useState<ProtocolPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Live preview (debounced) reflecting unsaved overrides — not persisted here.
  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const p = await getProtocolPreview(matter.id, { intro, contract_number: num, contract_date: date });
        if (!cancelled) { setPreview(p); setLoading(false); }
      } catch (e: any) {
        if (!cancelled) { setErr(e?.response?.data?.detail || 'Не удалось загрузить предпросмотр'); setLoading(false); }
      }
    }, 350);
    return () => { cancelled = true; window.clearTimeout(t); };
  }, [matter.id, intro, num, date]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const download = async () => {
    setErr(null);
    // Open the native "save as" picker FIRST, before any network await, so the
    // click's user-activation isn't consumed (else the dialog throws and we'd
    // silently fall back to a default-location download).
    type SaveHandle = { createWritable: () => Promise<{ write: (d: Blob) => Promise<void>; close: () => Promise<void> }> };
    const win = window as unknown as { showSaveFilePicker?: (o: unknown) => Promise<SaveHandle> };
    let handle: SaveHandle | null = null;
    if (typeof win.showSaveFilePicker === 'function') {
      try {
        handle = await win.showSaveFilePicker({
          suggestedName: `Протокол разногласий - ${matter.title || matter.id}.docx`,
          types: [{ description: 'Word', accept: { 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'] } }],
        });
      } catch (e) {
        if ((e as { name?: string })?.name === 'AbortError') return; // user cancelled
        handle = null; // unsupported/blocked -> fall back to normal download
      }
    }
    setBusy(true);
    try {
      // Persist edits so the downloaded DOCX matches the preview, then download.
      const updated = await patchMatter(matter.id, { protocol_intro: intro, contract_number: num, contract_date: date });
      onSaved(updated);
      if (handle) {
        try {
          const { blob } = await getProtocolBlob(matter.id);
          const writable = await handle.createWritable();
          await writable.write(blob);
          await writable.close();
          return;
        } catch { /* fall through to plain download */ }
      }
      window.location.href = protocolUrl(matter.id);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Не удалось скачать');
    } finally { setBusy(false); }
  };

  const inputCls = 'mt-0.5 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-pink-400/50';

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-700">Протокол разногласий — предпросмотр</p>
          <button onClick={onClose} title="Закрыть" className="text-gray-400 hover:text-gray-600 p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="px-4 py-3 border-b border-gray-100 grid sm:grid-cols-2 gap-2">
          <label className="text-xs text-gray-500">№ договора
            <input value={num} onChange={(e) => setNum(e.target.value)} className={inputCls} />
          </label>
          <label className="text-xs text-gray-500">Дата договора
            <input value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} />
          </label>
          <label className="text-xs text-gray-500 sm:col-span-2">Преамбула
            <textarea value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="Напр.: Все пункты принимаются в редакции Поставщика."
              className={`${inputCls} h-16 resize-none`} />
          </label>
        </div>

        <div className="flex-1 overflow-auto p-5 bg-gray-50">
          {!preview && loading ? (
            <div className="text-center py-10"><Loader2 className="w-7 h-7 animate-spin mx-auto text-pink-500" /></div>
          ) : preview ? (
            <div className="bg-white mx-auto max-w-3xl border border-gray-200 rounded-lg p-6 font-serif text-[14px] leading-relaxed text-gray-800">
              <p className="text-center font-bold uppercase">{preview.title}</p>
              <p className="text-center font-bold mb-4">{preview.subtitle}</p>
              <p><b>{preview.counter_label}:</b> {preview.counterparty}.</p>
              <p className="mb-3"><b>{preview.our_label}:</b> {preview.our_party}.</p>
              <p className="text-justify mb-4">{preview.intro}</p>
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr>
                    {preview.columns.map((col, i) => (
                      <th key={i} className="border border-gray-400 bg-gray-100 px-2 py-1 font-bold text-center">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.length === 0 ? (
                    <tr><td colSpan={3} className="border border-gray-400 px-2 py-3 text-center text-gray-400">Нет пунктов в протоколе</td></tr>
                  ) : preview.rows.map((r, i) => (
                    <tr key={i}>
                      <td className="border border-gray-400 px-2 py-1 text-center align-top w-16">{r.number}</td>
                      <td className="border border-gray-400 px-2 py-1 text-center align-top w-32">{r.counter}</td>
                      <td className={`border border-gray-400 px-2 py-1 text-justify align-top whitespace-pre-wrap ${r.our_bold ? 'font-bold' : ''}`}>{r.our}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex justify-between mt-8 text-[13px]">
                <div>{preview.sign_counter}<div className="mt-6">_______________ /____________/</div></div>
                <div>{preview.sign_our}<div className="mt-6">_______________ /____________/</div></div>
              </div>
            </div>
          ) : null}
          {err && <p className="text-xs text-red-600 mt-2">{err}</p>}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Закрыть</Button>
          <Button onClick={download} loading={busy}><FileDown className="w-4 h-4" /> Скачать DOCX</Button>
        </div>
      </div>
    </div>
  );
}

export default function MatterPage() {
  const { id } = useParams();
  const matterId = Number(id);
  const [matter, setMatter] = useState<MatterDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [showProtocol, setShowProtocol] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setMatter(await getMatter(matterId)); }
      catch (e: any) { setError(e?.response?.data?.detail || 'Не удалось загрузить дело'); }
      finally { setLoading(false); }
    })();
  }, [matterId]);

  const patchLocal = (id: number, partial: Partial<Clause>) => {
    setMatter((prev) => prev
      ? { ...prev, clauses: prev.clauses.map((x) => x.id === id ? { ...x, ...partial } : x) }
      : prev);
  };

  const reload = async () => { setMatter(await getMatter(matterId)); };

  const saveMatter = async (patch: Parameters<typeof patchMatter>[1]) => {
    try { setMatter(await patchMatter(matterId, patch)); }
    catch (e: any) { setError(e?.response?.data?.detail || 'Не удалось сохранить'); }
  };

  const allClauses = matter?.clauses || [];
  // A top-level number (no dot) whose substance lives in sub-clauses (5 -> 5.1,
  // 5.2 ...) is a section heading, not a risk/non-risk item.
  const isHeader = (c: Clause) =>
    !c.is_risk && !!c.number && !c.number.includes('.') &&
    allClauses.some((o) => o.id !== c.id && o.number.startsWith(c.number + '.'));

  const visible = allClauses.filter((c) => {
    if (filter === 'risks') return c.is_risk;
    if (filter === 'protocol') return c.include_in_protocol;
    return true; // all
  });

  if (loading) return <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin mx-auto text-pink-500" /></div>;
  if (error) return (
    <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" /><p className="text-sm text-red-600">{error}</p>
    </div>
  );
  if (!matter) return null;

  return (
    <>
      <Link to="/matters" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-pink-600 mb-3">
        <ArrowLeft className="w-4 h-4" /> К списку дел
      </Link>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-800">{matter.title || 'Без названия'}</h2>
            <p className="text-sm text-gray-500">
              {matter.counterparty && <>Контрагент: {matter.counterparty} · </>}Позиция: {matter.role}
              {matter.overall_risk && <> · Общий риск: {matter.overall_risk}</>}
            </p>
          </div>
          <Button onClick={() => setShowProtocol(true)} className="flex-shrink-0">
            <FileDown className="w-4 h-4" /> Протокол разногласий
          </Button>
        </div>

        {matter.summary && <p className="text-sm text-gray-700 mt-2">{matter.summary}</p>}

        {matter.analysis_warning && (
          <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">{matter.analysis_warning}</p>
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-2 mt-3">
          <label className="text-xs text-gray-500">Статус
            <Select value={matter.status} onChange={(v) => saveMatter({ status: v })} className="mt-0.5"
              options={MATTER_STATUSES.map((s) => ({ value: s, label: s }))} />
          </label>
          <label className="text-xs text-gray-500">№ договора
            <input defaultValue={matter.contract_number || ''} onBlur={(e) => saveMatter({ contract_number: e.target.value })}
              className="mt-0.5 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-pink-400/50" />
          </label>
          <label className="text-xs text-gray-500">Дата договора
            <input defaultValue={matter.contract_date || ''} onBlur={(e) => saveMatter({ contract_date: e.target.value })}
              className="mt-0.5 w-full text-sm border border-gray-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-pink-400/50" />
          </label>
        </div>
        <label className="block text-xs text-gray-500 mt-2">Преамбула протокола
          <textarea defaultValue={matter.protocol_intro || ''} onBlur={(e) => saveMatter({ protocol_intro: e.target.value })}
            placeholder="Напр.: Все пункты принимаются в редакции Поставщика."
            className="mt-0.5 w-full h-16 text-sm border border-gray-200 rounded-xl px-3 py-2 resize-none outline-none focus:ring-2 focus:ring-pink-400/50" />
        </label>

        <p className="text-xs text-gray-400 mt-2">
          Пунктов: {matter.clauses.length} · с рисками: {matter.clauses.filter((c) => c.is_risk).length}
          {' · '}в протоколе: {matter.clauses.filter((c) => c.include_in_protocol).length}
        </p>
      </div>

      <VersionsPanel matter={matter} onReload={reload} onMatterChanged={setMatter} />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <Button key={f.id} size="sm" variant={filter === f.id ? 'primary' : 'secondary'} onClick={() => setFilter(f.id)}>
            {f.label}
          </Button>
        ))}
      </div>

      <div className="space-y-2">
        {visible.map((c) => isHeader(c) ? (
          <div key={c.id} className="pt-4 pb-1 px-1">
            <h3 className="text-base font-bold text-gray-800">{c.original_text || c.current_text || `Раздел ${c.number}`}</h3>
          </div>
        ) : (
          <ClauseItem key={c.id} matterId={matterId} clause={c} onPatch={patchLocal} />
        ))}
        {visible.length === 0 && <p className="text-center text-gray-400 py-6">Нет пунктов в этом фильтре</p>}
      </div>

      <div className="mt-4">
        <MatterChat matter={matter} onMatterChanged={setMatter} />
      </div>

      <div className="mt-4">
        <ManagerPanel matter={matter} />
      </div>

      {showProtocol && (
        <ProtocolPreviewModal matter={matter} onClose={() => setShowProtocol(false)} onSaved={setMatter} />
      )}
    </>
  );
}
